import type { ElementType, HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Título del sistema. Define el ROL, no el tamaño.
 *
 * Existe porque el mismo rol estaba resuelto a ojo 65 veces en 6 combinaciones
 * distintas —13, 14, 15 y 16px, y la familia elegida sin criterio: 15px
 * aparecía 8 veces en Montserrat y 10 en Inter—. Con el rol nombrado, cambiar
 * la jerarquía de títulos es una edición acá y no 65 ediciones repartidas.
 *
 * La familia sale del rol y no de quien escribe la pantalla: los títulos de
 * jerarquía alta van en Montserrat (`--font-display`) y los de fila o tarjeta
 * en Inter (`--font-sans`), que es el texto de interfaz.
 *
 *   <Title role="section" as="h2">Cobranzas</Title>
 *   <Title role="row">{prospecto.nombre}</Title>
 */

export type TitleRole =
  /** Título de una fila de lista o de un ítem compacto. Inter. */
  | 'row'
  /** Título dentro de una tarjeta o panel. Inter. */
  | 'card'
  /** Título de un panel o de un bloque con identidad propia. Montserrat. */
  | 'panel'
  /** Título de una sección de página. Montserrat. */
  | 'section';

const ROLE_CLASS: Record<TitleRole, string> = {
  row: 'text-foreground text-body font-semibold leading-tight',
  card: 'text-foreground text-body-lg font-semibold leading-tight',
  panel: 'font-display text-foreground text-title font-semibold leading-tight',
  section: 'font-display text-foreground text-title font-semibold leading-tight',
};

interface TitleProps extends HTMLAttributes<HTMLElement> {
  role?: TitleRole;
  /** Elemento a renderizar. Elegilo por la jerarquía del documento, no por el
   *  tamaño: el tamaño ya lo da el rol. Default: `div`. */
  as?: ElementType;
  children: ReactNode;
}

export function Title({
  role = 'card',
  as: Tag = 'div',
  className,
  children,
  ...rest
}: TitleProps) {
  return (
    <Tag className={cn(ROLE_CLASS[role], className)} {...rest}>
      {children}
    </Tag>
  );
}
